const BRIDGE_ID = "sf-print-frame";
const BRIDGE_URL = "/sf-print/index.html";
const REQUEST_TIMEOUT = 60000;

let bridgeFrame = null;
let bridgeReadyPromise = null;
let isBridgeReady = false;
let resolveBridgeReady = null;
let requestSeed = 0;
const pendingRequests = {};

function createRequestId() {
    requestSeed += 1;
    return `sf-print-${Date.now()}-${requestSeed}`;
}

function ensureBridgeFrame() {
    if (bridgeReadyPromise) {
        return bridgeReadyPromise;
    }

    bridgeReadyPromise = new Promise((resolve, reject) => {
        let frame = document.getElementById(BRIDGE_ID);

        if (!frame) {
            isBridgeReady = false;
            frame = document.createElement("iframe");
            frame.id = BRIDGE_ID;
            frame.src = BRIDGE_URL;
            // SCPPrint renders the label into DOM nodes inside this frame before
            // handing the result to C-Lodop. A display:none frame has no layout
            // and causes the generated print content to be blank.
            frame.style.cssText = "position:fixed;left:-10000px;top:-10000px;width:1000px;height:1000px;border:0;pointer-events:none;";
            frame.setAttribute("aria-hidden", "true");
            document.body.appendChild(frame);
        }

        bridgeFrame = frame;

        const timer = setTimeout(() => {
            resolveBridgeReady = null;
            bridgeReadyPromise = null;
            reject(new Error("SF print bridge load timeout"));
        }, REQUEST_TIMEOUT);

        resolveBridgeReady = () => {
            clearTimeout(timer);
            isBridgeReady = true;
            resolveBridgeReady = null;
            resolve(frame);
        };

        frame.onload = () => {
            frame.contentWindow && frame.contentWindow.postMessage({
                type: "SF_PRINT_BRIDGE_PING"
            }, window.location.origin);
        };

        if (isBridgeReady) {
            resolveBridgeReady();
            return;
        }

        if (frame.contentWindow && frame.contentDocument && frame.contentDocument.readyState === "complete") {
            frame.contentWindow.postMessage({
                type: "SF_PRINT_BRIDGE_PING"
            }, window.location.origin);
        }
    });

    return bridgeReadyPromise;
}

function postToBridge(type, payload, timeout = REQUEST_TIMEOUT) {
    return ensureBridgeFrame().then(frame => {
        return new Promise((resolve, reject) => {
            const requestId = createRequestId();
            const timer = setTimeout(() => {
                delete pendingRequests[requestId];
                reject(new Error("SF print bridge request timeout"));
            }, timeout);

            pendingRequests[requestId] = {
                resolve,
                reject,
                timer
            };

            frame.contentWindow.postMessage({
                type,
                requestId,
                payload
            }, window.location.origin);
        });
    });
}

window.addEventListener("message", event => {
    if (event.origin !== window.location.origin) {
        return;
    }

    const message = event.data || {};
    if (message.type === "SF_PRINT_BRIDGE_READY") {
        if (bridgeFrame && event.source !== bridgeFrame.contentWindow) {
            return;
        }
        isBridgeReady = true;
        resolveBridgeReady && resolveBridgeReady();
        return;
    }

    if (message.type !== "SF_PRINT_BRIDGE_RESULT" || !message.requestId) {
        return;
    }

    const pending = pendingRequests[message.requestId];
    if (!pending) {
        return;
    }

    clearTimeout(pending.timer);
    delete pendingRequests[message.requestId];

    if (message.error) {
        pending.reject(new Error(message.error));
        return;
    }

    pending.resolve(message.result);
});

function logSdkInitResult(result) {
    switch (result && result.code) {
        case 1:
            break;
        case 2:
            console.error("SF C-Lodop plugin is not installed or started, Plugin download URL: " + result.downloadUrl);
            break;
        case 3:
            console.error("SF C-Lodop print plug-in version needs to be upgraded, Plugin download URL: " + result.downloadUrl);
            break;
        case 4:
            console.error("The page has not been downloaded yet, please wait for a while..............");
            break;
        default:
            console.error("Unknown error code:", result && result.code);
            break;
    }
}

export function installPrintService(options) {
    const sdkParams = {
        env: options.env || "pro",
        partnerID: options.partnerID,
        notips: options.notips || false
    };

    const service = {
        projectCode: options.projectCode,

        print(printData, callback, printOptions) {
            postToBridge("SF_PRINT", {
                sdkParams,
                printData,
                options: printOptions || {}
            }).then(result => {
                callback && callback(result);
            }).catch(error => {
                console.error("SF print failed:", error);
                callback && callback({
                    code: 0,
                    msg: error.message || String(error)
                });
            });
        },

        getPrinters(callback) {
            postToBridge("SF_GET_PRINTERS", { sdkParams }).then(result => {
                callback && callback(result);
            }).catch(error => {
                console.error("Failed to get the printer list...................", error);
                callback && callback({
                    code: 0,
                    msg: error.message || String(error)
                });
            });
        },

        setPrinter(printer) {
            return postToBridge("SF_SET_PRINTER", {
                sdkParams,
                printer
            });
        }
    };

    postToBridge("SF_INIT", { sdkParams }).then(result => {
        logSdkInitResult(result);
        service.getPrinters(result => {
            if (result.code === 1) {
                const printers = result.printers || [];
                let printerIndex = 0;
                let hpPrinter = -1;
                let zebraPrinter = -1;

                printers.forEach((item, index) => {
                    if (item.name === "HP LaserJet Professional M1213nf MFP") {
                        hpPrinter = index;
                    } else if (item.name && item.name.indexOf("Zebra") >= 0) {
                        zebraPrinter = index;
                    }
                });

                if (zebraPrinter >= 0) {
                    printerIndex = zebraPrinter;
                } else if (hpPrinter >= 0) {
                    printerIndex = hpPrinter;
                }
                service.setPrinter(printerIndex);
            } else {
                console.error("Failed to get the printer list...................");
            }
        });
    }).catch(error => {
        console.error("SF SCPPrint init failed:", error);
    });

    return service;
}
